import { useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  areStationsDragReorderedAtom,
  checkIsAreaChangedByUi,
  checkIsPassengersChangedByUi,
  createErrorResult,
  createSuccessResult,
  deleteBookingsActionsTransform,
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  errors,
  hookFormContextAF,
  IRideDto,
  IRideMetadata,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  Log,
  logBoolean,
  RebuildRouteTypeEnum,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  rideHasNotPriceRateAtom,
  ridePassengerDeleteBookingStateConfirmAtom,
  ridePreviousAreasSnapshotAtom,
  ridePreviousMetadataSnapshotAtom,
  RideStatusEnum,
  RideUpdateModel,
  SimpleResult,
  simpleResultErrorEmptyResponse,
} from "@ryde/shared";

import { useCrudEditContext, useDropdownSourceContext } from "../../contexts";
import { useRidesNavigation } from "../../navigation";
import { ISaveRideParams } from "../hook-models";
import { useHighlightEdited } from "../use-highlight-edited";
import { useRideAdditionalCheck } from "./use-ride-additional-check";
import { useRideAreasSnapshot } from "./use-ride-areas-snapshot";
import { useRideEditCompareResults } from "./use-ride-edit-compare-results";
import { useRideFormCleanup } from "./use-ride-form-cleanup";
import { useRideFormFields } from "./use-ride-form-fields";
import { useRideMetadataSnapshot } from "./use-ride-metadata-snapshot";
import { useRideUpdateFieldsChanges } from "./use-ride-update-fields-changes";
import { useRidesActions } from "./use-rides-actions";

export const useRideEditUtils = () => {
  const {
    data: { isDuplicate },
  } = useCrudEditContext();
  const { t } = useTranslation();
  const [, startTransition] = useTransition();
  const { createRide, updateRide, fetchRides } = useRidesActions();
  const { isRidesRoute, isRidesEditOrAddRoute } = useRidesNavigation();
  const deleteBookingActions = useRecoilValue(
    ridePassengerDeleteBookingStateConfirmAtom,
  );
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );

  const areStationsDragReorderedPreviously = useRecoilValue(
    areStationsDragReorderedAtom,
  );
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);
  const ridePreviousAreasSnapshot = useRecoilValue(
    ridePreviousAreasSnapshotAtom,
  );
  const ridePreviousMetadataSnapshot = useRecoilValue(
    ridePreviousMetadataSnapshotAtom,
  );
  const { cleanupRideFormStateAtoms } = useRideFormCleanup();
  const { setHighlightedRows } = useHighlightEdited(RideCrudModeKey.uiUnit);
  const setRideHasNoPriceRate = useSetRecoilState(rideHasNotPriceRateAtom);
  const { mergeFormValuesToMetadata, prepareFormFieldsData } =
    useRideFormFields();
  const { checkAndResolveRideDto } = useRideAdditionalCheck();
  const setRideDtoGlobal = useSetRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const setInitialDataSnapshotDto = useSetRecoilState(
    editFormDataInitialSnapshotAF(RideCrudModeKeyTuple),
  );
  const { updateRidePreviousAreasSnapshot } = useRideAreasSnapshot();
  const { updateRidePreviousMetadataSnapshot } = useRideMetadataSnapshot();
  const { compareRidesDialogResult } = useRideEditCompareResults();
  const { invalidateDropdownItemsFromDto } = useDropdownSourceContext();
  const { isRideFieldsChanged } = useRideUpdateFieldsChanges();

  const validateRideSaveForm = async (
    dryRunSave?: boolean,
    isEdit?: boolean,
  ): Promise<{
    isFormValid: boolean;
    formValuesSnapshot?: Partial<IRideDto>;
  }> => {
    const isFormValid = await rideFormContext?.trigger();
    const formValuesSnapshot = rideFormContext?.getValues() as
      Partial<IRideDto> | undefined;
    Log.debug(
      `Ride form values snapshot (is edit:${logBoolean(
        isEdit,
      )}, is duplicate:${logBoolean(isDuplicate)}):`,
      formValuesSnapshot,
    );
    return {
      isFormValid: isFormValid || !!dryRunSave,
      formValuesSnapshot,
    };
  };

  const composeRideMetadata = (
    rideDto: Partial<IRideDto>,
    metadataDryRun?: IRideMetadata,
    formValuesSnapshot?: Partial<IRideDto>,
  ): IRideMetadata => {
    const metadata = metadataDryRun || { ...rideDto.metadata };

    if (rideDto.courseRouteType) {
      metadata.courseType = rideDto.courseRouteType;
    }

    // Add rebuildRouteType if it's not default and not already set in metadata
    if (
      !metadata.rebuildRouteType &&
      lastSelectedRebuildType &&
      lastSelectedRebuildType !== RebuildRouteTypeEnum.Default
    ) {
      metadata.rebuildRouteType = lastSelectedRebuildType;
    }

    mergeFormValuesToMetadata(formValuesSnapshot, metadata);

    if (rideDto.metadata?.directionsService) {
      metadata.directionsService = rideDto.metadata.directionsService;
    }

    const effectiveRouteRebuildAt =
      rideDto.metadata?.routeRebuildAt ?? lastRouteRebuildAt;
    if (effectiveRouteRebuildAt) {
      metadata.routeRebuildAt = effectiveRouteRebuildAt;
    }

    if (rideDto.metadata?.routeRebuildSource) {
      metadata.routeRebuildSource = rideDto.metadata.routeRebuildSource;
    }

    return metadata;
  };

  const dryRunApiApplyChangesConfirmed = async ({
    rideFromResponse,
    formValuesSnapshot,
    rideDto,
    skipChangedValuesConfirmation,
  }: {
    rideFromResponse: Partial<IRideDto>;
    formValuesSnapshot: Partial<IRideDto> | void;
    rideDto: Partial<IRideDto> | undefined;
    skipChangedValuesConfirmation: boolean | undefined;
  }): Promise<SimpleResult<void>> => {
    const compareResult = await compareRidesDialogResult({
      rideFromResponse,
      rideSnapshot: formValuesSnapshot as Partial<IRideDto>,
      rideDto,
      skipChangedValuesConfirmation,
    });

    if (!compareResult?.confirmed) {
      Log.info(t(errors.common.changedRejectedByUser));
      return createErrorResult(errors.common.changedRejectedByUser);
    }

    compareResult?.changesList?.forEach(item => {
      if (rideFormContext) {
        Log.debug(
          `Changes from api, field ${item.fieldName} changed to `,
          item.newValue,
        );
        rideFormContext.setValue(item.fieldName, item.newValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
    return createSuccessResult();
  };

  const updateRideDtoGlobalInDryRun =
    (rideFromResponse: Partial<IRideDto>) =>
    (rideState?: Partial<IRideDto>) => {
      if (!rideState) {
        return rideState;
      }

      const hasDraftFromState = rideState.status === RideStatusEnum.Draft;
      const hasNewFromResponse = rideFromResponse.status === RideStatusEnum.New;
      const hasSwitchedFromDraftToNew = hasDraftFromState && hasNewFromResponse;

      if (!hasSwitchedFromDraftToNew) {
        return rideState;
      }

      const updatedState: Partial<IRideDto> = { ...rideState };

      if (hasDraftFromState && rideFromResponse.priceRateId) {
        updatedState.status = RideStatusEnum.New;
        updatedState.priceRateId = rideFromResponse.priceRateId;
        updatedState.price = rideFromResponse.price;
      }

      if (rideFromResponse.supplierId !== rideState.supplierId) {
        updatedState.supplierId = rideFromResponse.supplierId;
        updatedState.supplierName = rideFromResponse.supplierName;
      }

      if (rideFromResponse.vehicleTypeId !== rideState.vehicleTypeId) {
        updatedState.vehicleTypeId = rideFromResponse.vehicleTypeId;
        updatedState.vehicleTypeName = rideFromResponse.vehicleTypeName;
      }

      if (rideFromResponse.driverId !== rideState.driverId) {
        updatedState.driverId = rideFromResponse.driverId;
        updatedState.driverName = rideFromResponse.driverName;
      }

      return updatedState;
    };

  const processApiResponse = async (
    rideApiResponse: SimpleResult<RideUpdateModel>,
    params: ISaveRideParams,
    formValuesSnapshot?: Partial<IRideDto>,
  ): Promise<SimpleResult<RideUpdateModel | void>> => {
    const {
      rideDto,
      skipChangedValuesConfirmation,
      dryRun: dryRunSave,
    } = params;

    if (!rideApiResponse.success) {
      return rideApiResponse;
    }

    const rideFromResponse = rideApiResponse.data;

    if (!rideFromResponse) {
      return simpleResultErrorEmptyResponse(t);
    }

    setRideHasNoPriceRate(!rideFromResponse.priceRateId);

    if (dryRunSave) {
      const dryRunChangesConfirmed = await dryRunApiApplyChangesConfirmed({
        rideFromResponse,
        formValuesSnapshot,
        rideDto,
        skipChangedValuesConfirmation,
      });

      if (!dryRunChangesConfirmed.success) {
        return dryRunChangesConfirmed;
      }

      const hasPendingConfirmationAlert =
        rideFromResponse.confirmationAlert !== undefined;

      // Defer applying dry-run ride state while confirmationAlert is pending.
      // Otherwise the map / site chips re-render heavily under the confirm modal
      // (e.g. cross-site passenger add) and the tab can freeze.
      if (!hasPendingConfirmationAlert) {
        updateRidePreviousAreasSnapshot(rideFromResponse);
        updateRidePreviousMetadataSnapshot(rideFromResponse);
        setRideDtoGlobal(updateRideDtoGlobalInDryRun(rideFromResponse));
      }

      // Pass the BE result through unchanged — preserves any half-success error
      // (supplier-send rejected) so the caller can toast it.
      return rideApiResponse;
    }

    // Resets form dirty atoms, including ridePostSaveTransitionAtom=false.
    // Footer save handlers re-set postSaveTransition=true after this returns
    // when they still need to bridge the save → close → reopen dirty window.
    // Do not reorder: cleanup must stay before setRideEditDtoAndFormContext,
    // and callers must set postSaveTransition only after await saveRide*.
    cleanupRideFormStateAtoms();
    setRideEditDtoAndFormContext(rideFromResponse);

    // Pass the BE result through unchanged — preserves any half-success error
    // (supplier-send rejected) so the caller can toast it.
    return rideApiResponse;
  };

  const prepareRideForApi = (
    params: ISaveRideParams,
    formValuesSnapshot?: Partial<IRideDto>,
    metadataComputed?: IRideMetadata,
  ): RideUpdateModel => {
    const {
      rideDto,
      sendToSupplier,
      forcedRideTiming,
      stationsDragReordered,
      dryRun,
      isReturnDtoForGrid,
    } = params;
    const isEdit = Boolean(rideDto.id);

    const isAreaChangedByUi = checkIsAreaChangedByUi({
      metadataComputed,
      ridePreviousAreasSnapshot,
    });

    const isPassengersChangedByUi = checkIsPassengersChangedByUi({
      metadataComputed,
      ridePreviousMetadataSnapshot,
    });
    return {
      ...prepareFormFieldsData(formValuesSnapshot, isDuplicate, isEdit),
      metadata: metadataComputed,
      bookingActions:
        isEdit && !dryRun
          ? deleteBookingsActionsTransform(deleteBookingActions)
          : undefined,
      dryRun: dryRun ? true : undefined,
      isReturnDtoForGrid: isReturnDtoForGrid ? true : undefined,
      sendToSupplier,
      forcedRideTiming,
      stationsDragReordered:
        stationsDragReordered || areStationsDragReorderedPreviously,
      isAreaChangedByUi,
      isPassengersChangedByUi,
      currentRideUIStatus: rideDto?.status ?? RideStatusEnum.New,
      currentRideUIStationsIds: rideDto?.metadata?.stationIds ?? [],
    };
  };

  const handleRideUpdateOrCreate = async ({
    rideDto,
    rideForApiSend,
    dryRunSave,
    formValuesSnapshot,
  }: {
    rideDto: Partial<IRideDto>;
    rideForApiSend: RideUpdateModel;
    dryRunSave?: boolean;
    formValuesSnapshot?: Partial<IRideDto>;
  }): Promise<SimpleResult<RideUpdateModel>> => {
    if (rideDto.id) {
      let composedApiDtoForApiSend: RideUpdateModel;
      if (dryRunSave) {
        composedApiDtoForApiSend = rideForApiSend;
      } else {
        const resolvedRideDtoResult = await checkAndResolveRideDto({
          rideDto,
          formValuesSnapshot,
        });

        if (!resolvedRideDtoResult.success) {
          return resolvedRideDtoResult;
        }

        const rideValuesInjectedByCheck = resolvedRideDtoResult.data;

        composedApiDtoForApiSend = {
          ...rideForApiSend,
          ...rideValuesInjectedByCheck,
          isRideFieldsChanged,
        };
      }
      Log.debug(
        "Composed api with injected validation for api send (dryRun=false)",
        rideForApiSend,
      );
      return updateRide({
        rideForApiSend: composedApiDtoForApiSend,
        rideId: rideDto.id,
      });
    } else {
      return createRide(rideForApiSend);
    }
  };

  const fetchAndResetFormRide = useEvent(async rideId => {
    const fetchRidesResult = await fetchRides({
      skipPageReset: true,
      backgroundAction: isRidesEditOrAddRoute,
      isBypassPreloader: isRidesEditOrAddRoute,
    });

    if (fetchRidesResult.success) {
      const rideDto = fetchRidesResult.data?.find(ride => ride.id === rideId);

      const isSavedRideStillOpen = rideFormContext?.getValues("id") === rideId;

      if (rideDto && isSavedRideStillOpen) {
        rideFormContext?.reset(rideDto);
        setRideDtoGlobal(rideDto);
      }
    }
  });

  const setRideEditDtoAndFormContext = (
    rideFromResponse: Partial<IRideDto>,
  ): void => {
    if (rideFromResponse.id) {
      setRideDtoGlobal(rideFromResponse);
      setInitialDataSnapshotDto(rideFromResponse);
      rideFormContext?.reset(rideFromResponse);
      setHighlightedRows([rideFromResponse.id]);
      updateRidePreviousAreasSnapshot(rideFromResponse);
      updateRidePreviousMetadataSnapshot(rideFromResponse);
    }

    if (isRidesRoute) {
      startTransition(() => {
        fetchAndResetFormRide(rideFromResponse.id);
      });
    }
  };

  const invalidateDropdownItems = (
    rideApiResponse: SimpleResult<RideUpdateModel>,
  ): void => {
    invalidateDropdownItemsFromDto({
      dto: rideApiResponse.data as Partial<IRideDto>,
      dtoDropdownInvalidates: [
        {
          path: "metadata.tags",
          dtoDropdownValueToDropdownItemValue: "label",
        },
        {
          path: "journeyId",
          dtoDropdownValueToDropdownItemValue: "value",
        },
      ],
    });
  };

  return {
    validateRideSaveForm,
    composeRideMetadata,
    processApiResponse,
    prepareRideForApi,
    handleRideUpdateOrCreate,
    setRideEditDtoAndFormContext,
    invalidateDropdownItems,
  };
};
